/**
 * PlayersBadge (05.09, владелец): ОДНА стандартная кнопка «количество игроков» —
 * человечек + число, везде одинаковая (размер sm/md). Кликабельна только там, где
 * у сотрудника есть доступ к данным игроков (тогда открывает ClientInfoCard по брони);
 * иначе — просто индикатор без клика, без наведения, без «›».
 *
 * Доступ (зеркало сервера routes/clients.ts by-booking): роли dc/oo/eo — всегда;
 * оператор — только при грейде ≥ 3 (GET /api/grades/my, один запрос на сессию);
 * allow — экран сам знает, что доступ есть (админ зала — игры своей смены, 28.09).
 *
 *   <PlayersBadge count={g.players} bookingId={g.bookingId} />          — md, кликабельна при доступе
 *   <PlayersBadge count={r.players} size="sm" />                        — индикатор (без bookingId)
 *
 * count — то число, которое показывает экран (бронь или факт из отчёта) — компонент
 * его не интерпретирует. Клик гасит всплытие, чтобы не срабатывал клик по карточке.
 */
import { useEffect, useState } from "react";
import { useAuth } from "../../auth";
import { api } from "../../api/client";
import { ClientInfoCard } from "../modal/ClientInfoCard";
import { GameDots, useGameDots } from "./GameDots";
import css from "./PlayersBadge.module.css";

/* кэш на сессию: оператору грейд ≥3 открывает карточку игроков (канон OperatorScreen) */
let gradeOk: boolean | null = null;
let gradeReq: Promise<boolean> | null = null;
function operatorHasGrade3(): Promise<boolean> {
  if (gradeOk !== null) return Promise.resolve(gradeOk);
  gradeReq ??= api<{ results?: { grade?: number | string }[] }>("/api/grades/my")
    .then((r) => (r.results ?? []).some((g) => Number(g.grade) >= 3))
    .catch(() => false)
    .then((v) => { gradeOk = v; return v; });
  return gradeReq;
}

/** Есть ли у текущего сотрудника доступ к данным игроков (для кликабельности бейджа). */
export function usePlayersAccess(): boolean {
  const { user } = useAuth();
  const roles = user?.roles?.length ? user.roles : user?.role ? [user.role] : [];
  const privileged = roles.some((r) => r === "dc" || r === "oo" || r === "eo");
  const [g3, setG3] = useState<boolean>(gradeOk === true);
  useEffect(() => {
    if (privileged || !user) return;
    let alive = true;
    void operatorHasGrade3().then((v) => { if (alive) setG3(v); });
    return () => { alive = false; };
  }, [privileged, user]);
  return privileged || g3;
}

const Icon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="6" cy="4" r="2" />
    <path d="M2 11v-1a3 3 0 0 1 3-3h2a3 3 0 0 1 3 3v1" />
  </svg>
);

export interface PlayersBadgeProps {
  /** число игроков, которое показывает экран (бронь или факт) */
  count: number | string | null | undefined;
  /** 08.09 (владелец): факт из отчёта оператора — бейдж показывает «по брони → фактически»
   *  (агрегированные отчёты ОО). null/undefined — обычная форма с одним числом. */
  actual?: number | null;
  /** бронь для карточки клиента; без него бейдж — только индикатор */
  bookingId?: number | null;
  /** имя клиента для шапки карточки, пока профиль грузится */
  fallbackName?: string | null;
  /** экран сам показывает карточку (например, с историей действий) — бейдж только зовёт */
  onOpen?: (bookingId: number) => void;
  size?: "sm" | "md";
  /** 28.09: доступ дан экраном (админ зала по играм своей смены — сервер пускает так же) */
  allow?: boolean;
  className?: string;
  title?: string;
}

export function PlayersBadge({ count, actual, bookingId, fallbackName, onOpen, size = "md", allow, className, title }: PlayersBadgeProps) {
  const canOpen = usePlayersAccess() || !!allow;
  const [open, setOpen] = useState(false);
  const n = Number(count) || 0;
  /* 08.09 (владелец): расширенная форма для агрегированных отчётов ОО — «по брони → фактически» */
  const a = actual == null ? null : Number(actual) || 0;
  const clickable = canOpen && !!bookingId;
  /* 30.09 (владелец): точки внутри овала — по одной на каждую СЫГРАННУЮ с клиентом игру (shared/GameDots) */
  const dots = useGameDots(bookingId, clickable);
  const cls = `${css.badge} ${size === "sm" ? css.sm : css.md} ${clickable ? css.click : ""} ${className ?? ""}`;
  const inner = (
    <>
      <Icon /><b>{n}</b>
      {a != null ? (<><span className={css.arr} aria-hidden="true">→</span><b className={a > n ? css.more : a < n ? css.less : css.same}>{a}</b></>) : null}
      <GameDots n={dots} />
    </>
  );
  const played = dots > 0 ? `, сыграно с нами игр: ${dots}` : "";
  const label = (a == null ? `Игроков: ${n}` : `Игроков: по брони ${n}, фактически по отчёту ${a}`) + played;
  const hint = a == null ? undefined
    : `По брони ${n} → фактически ${a}` + (a === n ? " (совпало)" : a > n ? ` (+${a - n})` : ` (−${n - a})`);
  return (
    <>
      {clickable ? (
        <button type="button" className={cls} title={title ?? hint ?? "Информация об игроках" + (dots > 0 ? ` · сыграно с нами игр: ${dots}` : "")} aria-label={`${label} — информация`}
          onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(bookingId); else setOpen(true); }}>{inner}</button>
      ) : (
        <span className={cls} title={title ?? hint} aria-label={label}>{inner}</span>
      )}
      {open && bookingId ? <ClientInfoCard bookingId={bookingId} fallbackName={fallbackName ?? undefined} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
